import type { QueryClient } from "@tanstack/react-query";
import { WEBSITE_ID } from "../projectConfig";
import {
    Appointment,
    AppointmentStatus,
    Employee,
    appointmentKey,
    isBlocker,
} from "../types";
import { fetchEmployeeById, fetchEmployeeSchedule, getAppointmentsByIds } from "../firebase/firebase-delegate";
import { updateScheduleBitString } from "../time/timeUtils";

/**
 * GOD CACHE DELEGATE
 *
 * Single place that owns ALL setQueryData/removeQueries for:
 * - appointment entity cache (appointmentKey)
 * - currentEmployee cache (appointments map)
 * - employeeSchedule cache (Map<MMDDYYYY, bitstring>)
 * - optional appointments-by-employee list cache
 *
 * RULE:
 * Screens/components should call these functions and NOT write setQueryData themselves.
 */

/** -----------------------
 *  Query key helpers
 *  ----------------------*/

export const employeeScheduleKey = (employeeId: string) =>
    ["employeeSchedule", employeeId] as const;

export const currentEmployeeKey = ["currentEmployee"] as const;

/**
 * If you standardized on this key shape elsewhere:
 *   ['appointmentsByEmployee', employeeId, appointmentIdsKey]
 *
 * You can pass appointmentIdsKey as:
 * - a stable string
 * - or an array that you keep stable via useMemo (less ideal)
 */
export const appointmentsByEmployeeKey = (employeeId: string, appointmentIdsKey: unknown) =>
    ["appointmentsByEmployee", employeeId, appointmentIdsKey] as const;

export type EmployeeScheduleQueryKey = ReturnType<typeof employeeScheduleKey>;
export type AppointmentsByEmployeeQueryKey = ReturnType<typeof appointmentsByEmployeeKey>;

/** -----------------------
 *  Core cache mutations
 *  ----------------------*/
// The use cases are:
// - ADD: add new appointment
// - REMOVE: delete appointment
// - REPLACE: replace existing appointment (ex: move/resize)
export const enum ChangeMode {
    ADD = "add",
    REMOVE = "remove",
    REPLACE = "replace",
}

/**
 * Controls whether we ALSO mirror changes into:
 * - appointmentsByEmployee list cache (if you maintain one)
 * - schedule bitstring Map cache
 *
 */
export type CacheDelegateOptions = {
    siteId?: string;

    /** Optional query key for appointments-by-employee list cache */
    appointmentsQueryKey?: AppointmentsByEmployeeQueryKey;

    /** Optional query key for employee schedule cache (Map) */
    scheduleKey?: EmployeeScheduleQueryKey;

    /**
     * When true, we assume Firestore already updated schedule bitstrings
     * (ex: server wrote schedule, or your delegate wrote schedule & mutated scheduleBitStrings map).
     * In that case we skip local schedule bitstring math to avoid double applying.
     */
    serverAlreadyUpdatedScheduleBitstrings?: boolean;

    /**
     * When false, skip mutating ['currentEmployee'] appointment index.
     * Useful when admins are editing another employee's calendar.
     */
    updateCurrentEmployeeIndex?: boolean;
};

const stripDateKeyIfEmpty = (map: Record<string, string[]>, dateKey: string) => {
    const list = map[dateKey] ?? [];
    if (list.length === 0) {
        const { [dateKey]: _, ...rest } = map;
        return rest;
    }
    return map;
};

const addIdToDate = (map: Record<string, string[]>, dateKey: string, id: string) => {
    const existing = map[dateKey] ?? [];
    if (existing.includes(id)) return map;
    return { ...map, [dateKey]: [...existing, id] };
};

const removeIdFromDate = (map: Record<string, string[]>, dateKey: string, id: string) => {
    const existing = map[dateKey] ?? [];
    const remaining = existing.filter((x) => x !== id);
    const next = { ...map, [dateKey]: remaining };
    return stripDateKeyIfEmpty(next, dateKey);
};

const applyScheduleDelta = (
    scheduleMap: Map<string, string>,
    appointment: Appointment,
    cancel: boolean
): Map<string, string> => {
    const bitString = scheduleMap.get(appointment.appointmentDate);
    if (!bitString) return scheduleMap;

    const updated = updateScheduleBitString(
        bitString,
        appointment.appointmentTime,
        appointment.appointmentDuration,
        cancel
    );
    if (!updated) return scheduleMap;

    const next = new Map(scheduleMap);
    next.set(appointment.appointmentDate, updated);
    return next;
};

/**
 * One function to rule them all:
 * - updates appointment entity cache (appointmentKey)
 * - updates currentEmployee.appointments map
 * - optionally updates appointmentsByEmployee list
 * - optionally updates employeeSchedule bitstring map
 */
export const applyAppointmentChange = (
    queryClient: QueryClient,
    mode: ChangeMode,
    next: Appointment,
    prev?: Appointment,
    options?: CacheDelegateOptions
) => {
    const siteId = options?.siteId ?? WEBSITE_ID;
    const key = appointmentKey(siteId, next.id);

    console.log(`applyAppointmentChange ${mode} ${next.id} ${next.appointmentDate}`);
    // 1) Appointment entity cache
    if (mode === ChangeMode.REMOVE) {
        queryClient.removeQueries({ queryKey: key, exact: true });
    } else {
        queryClient.setQueryData<Appointment>(key, next);
    }

    // 2) currentEmployee cache (appointments index map)
    if (options?.updateCurrentEmployeeIndex !== false) {
        queryClient.setQueryData<Employee | undefined>(currentEmployeeKey, (old) => {
            if (!old) return old;

            let updatedAppointments = { ...(old.appointments ?? {}) };

            if (mode === ChangeMode.ADD) {
                updatedAppointments = addIdToDate(updatedAppointments, next.appointmentDate, next.id);
            } else if (mode === ChangeMode.REMOVE) {
                updatedAppointments = removeIdFromDate(updatedAppointments, next.appointmentDate, next.id);
            } else if (mode === ChangeMode.REPLACE) {
                if (prev) {
                    updatedAppointments = removeIdFromDate(updatedAppointments, prev.appointmentDate, prev.id);
                }
                updatedAppointments = addIdToDate(updatedAppointments, next.appointmentDate, next.id);
            }

            return { ...old, appointments: updatedAppointments };
        });
    }

    // 3) appointmentsByEmployee list cache (optional)
    if (options?.appointmentsQueryKey) {
        queryClient.setQueryData<Appointment[] | undefined>(options.appointmentsQueryKey, (old) => {
            if (!old) return old;

            if (mode === ChangeMode.REMOVE) {
                return old.filter((a) => a.id !== next.id);
            }

            if (mode === ChangeMode.ADD) {
                const exists = old.some((a) => a.id === next.id);
                return exists ? old : [...old, next];
            }

            if (mode === ChangeMode.REPLACE) {
                return old.map((a) => (a.id === next.id ? next : a));
            }

            return old;
        });
    }

    // 4) schedule bitstring map cache (optional)
    if (!options?.scheduleKey) return;
    if (options.serverAlreadyUpdatedScheduleBitstrings) {
        // Server delegate already mutated the shared schedule Map; clone so subscribers get a new reference.
        queryClient.setQueryData<Map<string, string> | undefined>(options.scheduleKey, (old) =>
            old ? new Map(old) : old
        );
        return;
    }

    queryClient.setQueryData<Map<string, string> | undefined>(options.scheduleKey, (old) => {
        if (!old) return old;

        // add/remove = apply delta for this appointment
        if (mode === ChangeMode.ADD) {
            return applyScheduleDelta(old, next, /*cancel=*/false);
        }
        if (mode === ChangeMode.REMOVE) {
            return applyScheduleDelta(old, next, /*cancel=*/true);
        }

        // replace (move/resize) = cancel old then add new
        let updated = old;
        if (prev) {
            updated = applyScheduleDelta(updated, prev, /*cancel=*/true);
        }
        updated = applyScheduleDelta(updated, next, /*cancel=*/false);
        return updated;
    });
};

/**
 * Remove an appointment from employee-related caches without touching appointment entity cache.
 * Use this when the appointment still exists (e.g., staff handoff).
 */
export const removeAppointmentFromEmployeeCaches = (
    queryClient: QueryClient,
    appointment: Appointment,
    options?: CacheDelegateOptions
) => {
    // 1) currentEmployee cache (appointments index map)
    queryClient.setQueryData<Employee | undefined>(currentEmployeeKey, (old) => {
        if (!old) return old;
        const updatedAppointments = removeIdFromDate(
            { ...(old.appointments ?? {}) },
            appointment.appointmentDate,
            appointment.id
        );
        return { ...old, appointments: updatedAppointments };
    });

    // 2) appointmentsByEmployee list cache (optional)
    if (options?.appointmentsQueryKey) {
        queryClient.setQueryData<Appointment[] | undefined>(options.appointmentsQueryKey, (old) => {
            if (!old) return old;
            return old.filter((a) => a.id !== appointment.id);
        });
    }

    // 3) schedule bitstring map cache (optional)
    if (!options?.scheduleKey) return;
    if (options.serverAlreadyUpdatedScheduleBitstrings) {
        queryClient.setQueryData<Map<string, string> | undefined>(options.scheduleKey, (old) =>
            old ? new Map(old) : old
        );
        return;
    }

    queryClient.setQueryData<Map<string, string> | undefined>(options.scheduleKey, (old) => {
        if (!old) return old;
        return applyScheduleDelta(old, appointment, /*cancel=*/true);
    });
};

/**
 * Use this when you cancel/remove a bunch at once from ManageSchedule.
 * Your rule:
 * - blockers: removed from caches entirely
 * - real appointments: keep but set status = CANCELLED (and optionally remove from employee.appointments map)
 * Provide appointmentsQueryKey / scheduleKey if you want matching caches to stay in sync.
 *
 * NOTE: Your current code removes IDs from employee.appointments map for cancelled real appts.
 * If you want that behavior, keep it true.
 */
export const applyAppointmentCancelledOrDeleted = (
    queryClient: QueryClient,
    cancelled: Appointment[],
    options?: CacheDelegateOptions & {
        /** if true, cancelled real appointments are removed from employee.appointments map */
        removeCancelledFromEmployeeIndex?: boolean;
    }
) => {
    if (!cancelled.length) return;
    const siteId = options?.siteId ?? WEBSITE_ID;

    // 1) Update each appointment entity cache appropriately
    for (const appt of cancelled) {
        const key = appointmentKey(siteId, appt.id);
        if (isBlocker(appt)) {
            queryClient.removeQueries({ queryKey: key, exact: true });
        } else {
            queryClient.setQueryData<Appointment>(key, { ...appt, status: AppointmentStatus.CANCELLED });
        }
    }

    // 2) Update appointmentsByEmployee list cache (optional)
    if (options?.appointmentsQueryKey) {
        const cancelledById = new Set(cancelled.map((a) => a.id));

        queryClient.setQueryData<Appointment[] | undefined>(options.appointmentsQueryKey, (old) => {
            if (!old) return old;
            return old
                .map((a) => {
                    if (!cancelledById.has(a.id)) return a;
                    // If this cancelled thing was a blocker, drop it from list
                    const cancelledMatch = cancelled.find((x) => x.id === a.id);
                    if (cancelledMatch && isBlocker(cancelledMatch)) return null;
                    return { ...a, status: AppointmentStatus.CANCELLED };
                })
                .filter((x): x is Appointment => x != null);
        });
    }

    // 3) Update currentEmployee index map (optional)
    if (options?.removeCancelledFromEmployeeIndex) {
        queryClient.setQueryData<Employee | undefined>(currentEmployeeKey, (old) => {
            if (!old?.appointments) return old;
            let updated = { ...old.appointments };
            for (const appt of cancelled) {
                updated = removeIdFromDate(updated, appt.appointmentDate, appt.id);
            }
            return { ...old, appointments: updated };
        });
    }

    // 4) Schedule cache updates (optional)
    if (!options?.scheduleKey) return;
    if (options.serverAlreadyUpdatedScheduleBitstrings) return;

    queryClient.setQueryData<Map<string, string> | undefined>(options.scheduleKey, (old) => {
        if (!old) return old;
        let nextMap = old;
        for (const appt of cancelled) {
            // cancelling frees time => cancel=true
            nextMap = applyScheduleDelta(nextMap, appt, /*cancel=*/true);
        }
        return nextMap;
    });
};

/**
 * If you just want to set/merge schedule cache for a date key.
 * Useful when you do a "set schedule for date" (ManageSchedule save).
 */
export const setScheduleCacheForDate = (
    queryClient: QueryClient,
    employeeId: string,
    dateKeyMMDDYYYY: string,
    nextBitString: string
) => {
    queryClient.setQueryData<Map<string, string> | undefined>(
        employeeScheduleKey(employeeId),
        (old) => {
            if (!old) return old;
            const next = new Map(old);
            next.set(dateKeyMMDDYYYY, nextBitString);
            return next;
        }
    );
};


/**
 * Replaces the entire employeeSchedule cache map for an employee.
 * Use this when you *already* have a schedule map (override) and want every consumer
 * to read from it without a refetch.
 *
 * IMPORTANT: This assumes scheduleOverrideMap is a Map<string, string> where keys are MMDDYYYY.
 */
export const setScheduleOverrideMap = (
    queryClient: QueryClient,
    employeeId: string,
    scheduleOverrideMap: Map<string, string>
) => {
    if (!employeeId || !scheduleOverrideMap) return;

    queryClient.setQueryData<Map<string, string>>(
        ["employeeSchedule", employeeId],
        (previous) => {
            // If it's literally the same Map instance, do nothing.
            if (previous === scheduleOverrideMap) return previous;

            // If you want to be extra safe, avoid stomping a non-empty cache with an empty override:
            // (uncomment if desired)
            // if (previous && previous.size > 0 && scheduleOverrideMap.size === 0) return previous;

            return scheduleOverrideMap;
        }
    );
};

/**
 * Patch ONE day in the employeeSchedule map inside React Query cache.
 * - Reads the current bitstring for that dateKey (MMDDYYYY)
 * - Applies updateScheduleBitString(...)
 * - Writes back a new Map (immutability) so subscribers update
 *
 * Returns true if it successfully patched, false if it couldn't.
 */
export const applyScheduleBitstringPatch = (
    queryClient: QueryClient,
    employeeId: string,
    dateKey: string,         // MMDDYYYY
    appointmentTime: string, // HHmm
    appointmentDuration: number,
    cancel: boolean
): boolean => {
    if (!employeeId || !dateKey || !appointmentTime || !appointmentDuration) return false;

    let didPatch = false;

    queryClient.setQueryData<Map<string, string> | undefined>(
        ["employeeSchedule", employeeId],
        (old) => {
            if (!old) return old;

            const current = old.get(dateKey);
            if (!current) return old;

            const updated = updateScheduleBitString(
                current,
                appointmentTime,
                appointmentDuration,
                cancel
            );
            if (!updated) return old;

            const next = new Map(old);
            next.set(dateKey, updated);

            didPatch = true;
            return next;
        }
    );

    return didPatch;
};

/**
 * --------------------------------------------------------------------------------------
 * Appointment document cache helpers (per-appointment)
 * --------------------------------------------------------------------------------------
 */

export const upsertAppointmentIntoCache = (
    queryClient: QueryClient,
    appointment: Appointment,
    siteId: string = WEBSITE_ID
) => {
    queryClient.setQueryData<Appointment>(appointmentKey(siteId, appointment.id), appointment);
};

export const removeAppointmentFromCache = (
    queryClient: QueryClient,
    appointmentId: string,
    siteId: string = WEBSITE_ID
) => {
    queryClient.removeQueries({ queryKey: appointmentKey(siteId, appointmentId), exact: true });
};

/**
 * --------------------------------------------------------------------------------------
 * Employee appointment index cache helper (['currentEmployee'])
 *
 * Mirrors what you already do in CalendarScreen:
 * - add: put appt.id under appointment.appointmentDate (no duplicates)
 * - remove: remove id; if date array becomes empty, delete the date key
 * - replace: remove previous date entry, then add new date entry
 * --------------------------------------------------------------------------------------
 */
export type EmployeeIndexMode = "add" | "remove" | "replace";

export const updateEmployeeAppointmentsIndex = (
    queryClient: QueryClient,
    mode: EmployeeIndexMode,
    appointment: Appointment,
    previous?: Appointment
) => {
    queryClient.setQueryData<Employee | undefined>(["currentEmployee"], (old) => {
        if (!old) return old;

        let updatedAppointments = { ...(old.appointments ?? {}) };

        const addToDate = (date: string, id: string) => {
            const existing = updatedAppointments[date] || [];
            if (existing.includes(id)) return;
            updatedAppointments = { ...updatedAppointments, [date]: [...existing, id] };
        };

        const removeFromDate = (date: string, id: string) => {
            const existing = updatedAppointments[date] || [];
            const remaining = existing.filter((x) => x !== id);
            if (remaining.length > 0) {
                updatedAppointments = { ...updatedAppointments, [date]: remaining };
            } else {
                // delete key when empty
                const { [date]: _removed, ...rest } = updatedAppointments;
                updatedAppointments = rest;
            }
        };

        if (mode === "add") {
            addToDate(appointment.appointmentDate, appointment.id);
        } else if (mode === "remove") {
            removeFromDate(appointment.appointmentDate, appointment.id);
        } else if (mode === "replace" && previous) {
            removeFromDate(previous.appointmentDate, previous.id);
            addToDate(appointment.appointmentDate, appointment.id);
        }

        return { ...old, appointments: updatedAppointments };
    });
};

/**
 * --------------------------------------------------------------------------------------
 * Schedule cache helpers (['employeeSchedule', employeeId]) where value is Map<MMDDYYYY, bitstring>
 * --------------------------------------------------------------------------------------
 */

export const scheduleKey = (employeeId: string) => ["employeeSchedule", employeeId] as const;


/**
 * Sets a single day's bitstring to a specific value (used when you already know the final day bitstring).
 * (This is what ManageScheduleViewModel wants after updateEmployeeSchedule_WithBitStringData succeeds.)
 */
export const setScheduleDayBitstring = (
    queryClient: QueryClient,
    employeeId: string,
    dateKeyMMDDYYYY: string,
    bitString: string
) => {
    queryClient.setQueryData<Map<string, string> | undefined>(scheduleKey(employeeId), (old) => {
        if (!old) return old;
        const next = new Map(old);
        next.set(dateKeyMMDDYYYY, bitString);
        return next;
    });
};

/**
 * --------------------------------------------------------------------------------------
 * Hydration helpers (you already created these, moved here so everything is in one place)
 * --------------------------------------------------------------------------------------
 */

// Hydrate cache with appointment docs for the given IDs (only missing ones).
export const hydrateAppointmentsIntoCache = async (
    queryClient: QueryClient,
    ids: string[],
    siteId: string = WEBSITE_ID
) => {
    const clean = Array.from(new Set(ids.filter((x) => x && x.trim())));
    if (!clean.length) return { hydrated: 0, total: 0 };

    const missing = clean.filter((id) => queryClient.getQueryData(appointmentKey(siteId, id)) == null);
    if (!missing.length) return { hydrated: 0, total: clean.length };
    console.log(`Hydrating ${missing.length} missing appointments...`);

    const fetched = await getAppointmentsByIds(missing, siteId, 'cache-delegate hydrateAppointments');
    fetched.forEach((appt) => {
        upsertAppointmentIntoCache(queryClient, appt, siteId);
    });

    return { hydrated: fetched.length, total: clean.length };
};

// Refetch a single appointment by ID and update cache (or delete cache if missing).
export const refetchAppointmentIntoCache = async (
    queryClient: QueryClient,
    appointmentId: string,
    siteId: string = WEBSITE_ID
) => {
    const result = await refetchAppointmentIntoCacheWithStatus(queryClient, appointmentId, siteId);
    if (result.status === 'ok') {
        return result.appointment;
    }
    if (result.status === 'not_found') {
        return null;
    }
    throw result.error;
};

export type RefetchAppointmentResult =
    | { status: 'ok'; appointment: Appointment }
    | { status: 'not_found' }
    | { status: 'error'; error: unknown };

export const refetchAppointmentIntoCacheWithStatus = async (
    queryClient: QueryClient,
    appointmentId: string,
    siteId: string = WEBSITE_ID
): Promise<RefetchAppointmentResult> => {
    const cleanId = appointmentId?.trim();
    if (!cleanId) return { status: 'not_found' };

    try {
        const [appointment] = await getAppointmentsByIds([cleanId], siteId, 'cache-delegate refetchAppointment');
        if (appointment) {
            upsertAppointmentIntoCache(queryClient, appointment, siteId);
            return { status: 'ok', appointment };
        }

        removeAppointmentFromCache(queryClient, cleanId, siteId);
        return { status: 'not_found' };
    } catch (error) {
        return { status: 'error', error };
    }
};

export type RecurringBreakCacheDelta = {
    createdBlockers: Appointment[];
    deletedBlockerIds: string[];
    cancelledAppointmentIds: string[];
};

type ApplyEmployeeSaveCacheOptions = {
    updatedEmployee: Employee;
    siteId?: string;
    regeneratedScheduleCache?: Map<string, string>;
    recurringBreakRegenerationResult?: RecurringBreakCacheDelta;
};

// Centralized cache patch for ManageEmployees save flow so page-level logic stays thin.
export const applyEmployeeSaveCache = (
    queryClient: QueryClient,
    {
        updatedEmployee,
        siteId = WEBSITE_ID,
        regeneratedScheduleCache,
        recurringBreakRegenerationResult,
    }: ApplyEmployeeSaveCacheOptions
) => {
    queryClient.setQueryData<Employee[]>(["allEmployees"], (previous = []) => (
        previous.map((employee) =>
            employee.id === updatedEmployee.id ? { ...employee, ...updatedEmployee } : employee
        )
    ));
    queryClient.setQueryData<Employee[]>(["employees"], (previous = []) => (
        previous.map((employee) =>
            employee.id === updatedEmployee.id ? { ...employee, ...updatedEmployee } : employee
        )
    ));
    queryClient.setQueryData<Employee | undefined>(currentEmployeeKey, (previous) => {
        if (!previous || previous.id !== updatedEmployee.id) {
            return previous;
        }
        return { ...previous, ...updatedEmployee };
    });

    if (regeneratedScheduleCache) {
        queryClient.setQueriesData<Map<string, string> | undefined>(
            { queryKey: employeeScheduleKey(updatedEmployee.id) },
            () => new Map(regeneratedScheduleCache)
        );
    }

    if (!recurringBreakRegenerationResult) {
        return;
    }

    recurringBreakRegenerationResult.createdBlockers.forEach((blocker) => {
        queryClient.setQueryData<Appointment>(appointmentKey(siteId, blocker.id), blocker);
    });
    recurringBreakRegenerationResult.deletedBlockerIds.forEach((appointmentId) => {
        queryClient.removeQueries({ queryKey: appointmentKey(siteId, appointmentId), exact: true });
    });
    recurringBreakRegenerationResult.cancelledAppointmentIds.forEach((appointmentId) => {
        queryClient.setQueryData<Appointment | undefined>(
            appointmentKey(siteId, appointmentId),
            (previous) => (previous ? { ...previous, status: AppointmentStatus.CANCELLED } : previous)
        );
    });
};

export const reconcileEmployeeCachesFromSource = async (
    queryClient: QueryClient,
    employeeId: string,
    siteId: string = WEBSITE_ID
) => {
    const [employee, schedule] = await Promise.all([
        fetchEmployeeById(employeeId, siteId),
        fetchEmployeeSchedule(employeeId, siteId),
    ]);

    if (employee) {
        queryClient.setQueryData<Employee[]>(["allEmployees"], (previous = []) => (
            previous.map((entry) => (entry.id === employee.id ? { ...entry, ...employee } : entry))
        ));
        queryClient.setQueryData<Employee[]>(["employees"], (previous = []) => (
            previous.map((entry) => (entry.id === employee.id ? { ...entry, ...employee } : entry))
        ));
        queryClient.setQueryData<Employee | undefined>(currentEmployeeKey, (previous) => {
            if (!previous || previous.id !== employee.id) {
                return previous;
            }
            return { ...previous, ...employee };
        });
    }

    queryClient.setQueriesData<Map<string, string> | undefined>(
        { queryKey: employeeScheduleKey(employeeId) },
        () => new Map(schedule)
    );

    return {
        employeeFound: Boolean(employee),
    };
};
